import { convertCssValueToResolvedValue, CssValueOptions } from './convertCssValueToResolvedValue';

type CssColorOptions = Omit<CssValueOptions, 'property'>;

const FALLBACK_COLOR = '#000000ff';

const cache = new Map<string, string>();

/** Call this when the theme changes to force re-resolution of CSS vars. */
export function clearCssColorCache(): void {
  cache.clear();
}

function convertSingleColorToHexa(color: string): string {
  const rgbMatch = color.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
  const rgbaMatch = color.match(/^rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)$/);

  if (rgbMatch || rgbaMatch) {
    const match = rgbMatch || rgbaMatch;
    if (!match) return FALLBACK_COLOR;

    const r = parseInt(match[1], 10);
    const g = parseInt(match[2], 10);
    const b = parseInt(match[3], 10);
    const a = match[4] ? Math.round(parseFloat(match[4]) * 255) : 255;

    const toHex = (n: number): string => {
      const hex = n.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}${toHex(a)}`;
  }

  if (color.startsWith('#')) {
    let hex = color.slice(1);

    if (hex.length === 3) {
      hex = hex
        .split('')
        .map(char => char + char)
        .join('');
    }

    if (hex.length === 6) {
      hex += 'ff';
    }

    return `#${hex}`;
  }

  return FALLBACK_COLOR;
}

export function convertCssValueToHexaColor(cssValue: string, options: CssColorOptions = {}): string {
  const elementId = options.element ? options.element.id || undefined : 'root';
  const cacheKey = elementId !== undefined ? `${cssValue}::${elementId}` : undefined;
  if (cacheKey !== undefined) {
    const cached = cache.get(cacheKey);
    if (cached !== undefined) return cached;
  }
  const setCache = cacheKey !== undefined ? (value: string) => cache.set(cacheKey, value) : undefined;

  const resolvedValue = convertCssValueToResolvedValue(cssValue, {
    ...options,
    property: 'background'
  });

  const withHexaColors = resolvedValue.replace(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/g, match =>
    convertSingleColorToHexa(match)
  );

  const gradientMatch = withHexaColors.match(/(?:linear|radial)-gradient\([^)]+\)/);
  if (gradientMatch) {
    setCache?.(gradientMatch[0]);
    return gradientMatch[0];
  }

  const colorValue = convertCssValueToResolvedValue(cssValue, {
    ...options,
    property: 'color'
  });
  const result = convertSingleColorToHexa(colorValue);
  setCache?.(result);
  return result;
}
